// Shared helpers for the HPU and TEBA Payment Receive pages (no UI here).
import { getAuthToken } from "../../../utils/authStorage";

export const METHODS_NEEDING_REFERENCE = ["bank_transfer", "card"];

export const formatLabel = (value) =>
  ({
    bank_transfer: "Bank Transfer",
    card: "Card",
    cash: "Cash",
    hpc_admin: "HPC Admin",
    online: "Online",
    qr_payment: "QR Payment",
  }[String(value || "").toLowerCase()] || (value || "")
    .toString()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" "));

export const displayValue = (value) => (value === null || value === undefined || value === "" ? "—" : value);

export const todayAsInputValue = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const newTransactionId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (character) => {
    const random = Math.floor(Math.random() * 16);
    const value = character === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

export const authConfig = () => {
  const token = getAuthToken();
  return {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };
};

export const amountInputValue = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return "";
  return Number.isInteger(number) ? String(number) : number.toFixed(2);
};

/** Error text for an amount field, or "" when it is a positive amount with at most 2 decimals. */
export const amountError = (text) => {
  const amount = String(text || "").trim();
  if (!amount) return "Payment amount is required.";
  if (!/^\d+(\.\d{1,2})?$/.test(amount) || Number(amount) <= 0) {
    return "Enter a positive amount with no more than 2 decimal places.";
  }
  return "";
};

/** Saves a blob response as a file. */
export const saveBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// ---------------------------------------------------------------- printable receipt HTML
export const escapeHtml = (value) =>
  String(displayValue(value))
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

export const receiptRow = (label, value) => `
    <div class="row"><span>${escapeHtml(label)}</span><strong>${escapeHtml(value)}</strong></div>`;

export const receiptTotalRow = (label, value) =>
  `<div class="row total"><span>${label}</span><strong>${escapeHtml(value)}</strong></div>`;

export const receiptSection = (title, rowsHtml) => `<section class="section"><h2>${title}</h2>${rowsHtml}</section>`;

/** Full printable receipt page. ``brandHtml`` / ``subtitleHtml`` / ``body`` are already HTML. */
export const receiptDocument = ({ title, logoSrc, logoAlt, brandHtml, subtitleHtml, body }) => `<!DOCTYPE html>
<html><head><meta charset="UTF-8"><title>${escapeHtml(title)}</title>
<style>
*{box-sizing:border-box}body{font-family:Arial,sans-serif;color:#17202a;background:#fff;margin:0;padding:18mm}
.receipt{max-width:170mm;margin:0 auto}.header{text-align:center;border-bottom:2px solid #f5a623;padding-bottom:8mm;margin-bottom:7mm}
.logo{max-width:42mm;max-height:22mm;object-fit:contain;margin-bottom:3mm}.header h1{margin:0;font-size:22px}.header p{margin:2mm 0 0;color:#52606d}
.section{margin-bottom:6mm}.section h2{font-size:12px;text-transform:uppercase;letter-spacing:1px;color:#b66e00;border-bottom:1px solid #d9e2ec;padding-bottom:2mm}
.row{display:flex;justify-content:space-between;gap:10mm;padding:2mm 0;border-bottom:1px solid #edf1f5;font-size:11px}.row strong{text-align:right;word-break:break-word}
.total{font-size:16px;border-top:2px solid #17202a;border-bottom:2px solid #17202a;padding:4mm 0}.footer{text-align:center;margin-top:12mm;color:#52606d;font-size:10px}
@media print{@page{size:A4;margin:0}body{padding:15mm}}
</style></head><body><main class="receipt">
<header class="header"><img class="logo" src="${escapeHtml(logoSrc)}" alt="${logoAlt}"><h1>${brandHtml}</h1><p>${subtitleHtml}</p></header>
${body}
<footer class="footer">Thank you for your payment.<br>This is a computer generated receipt.</footer></main></body></html>`;

export const publicAssetUrl = (path) => `${window.location.origin}${process.env.PUBLIC_URL || ""}${path}`;

/** Opens the receipt HTML in a new window and prints it. Returns false when pop-ups are blocked. */
export const printReceiptHtml = (html) => {
  const printWindow = window.open("", "_blank", "noopener,noreferrer");
  if (!printWindow) return false;
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.onload = () => printWindow.print();
  return true;
};
